import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ApiSettingsType, BeltType, ViewStudentType } from '../model/model'
import { RootState } from './store'

const sliceName = 'config'

type RatingResponseType = {
  id: string
  category: string
  response: Array<string>
}

export type FetchConfigResponseType = {
  config: {
    logged_in: boolean
    rate_video: boolean
    is_admin: boolean
    is_instructor: boolean
    rank?: number
    instructor_id?: number
    access_level?: string
    view_student?: ViewStudentType
    override_help_image?: string
    override_builder_image?: string
    override_video_image?: string
    override_live_image?: string
    override_upload_image?: string
    need_select_instructor: boolean
    instructors?: Array<{
      display_name: string
      first_name: string
      id: number
      last_name: string
    }>
    instructor_approved?: boolean
  }
  belts: Array<BeltType>
  errors: Array<string>
  ratingResponse: Array<RatingResponseType>
}

type SliceStateType = {
  setting?: ApiSettingsType
  fetching: boolean
  init: boolean
  ratingResponse: Array<RatingResponseType>
} & FetchConfigResponseType['config']

const initialState: SliceStateType = {
  init: false,
  fetching: false,
  logged_in: false,
  rate_video: false,
  is_admin: false,
  is_instructor: false,
  ratingResponse: [],
  need_select_instructor: false
}

export const fetchConfig = createAsyncThunk<FetchConfigResponseType, null>(
  `${sliceName}/fetch`,
  async (_args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}config`, {
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        }
      })
        .then((res) => res.json())
        .then((res: FetchConfigResponseType) => {
          return res
        })

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  }
)

type AddInstructorRequestType = {
  instructor_id: number
}

type AddInstructorResponseType = {
  result: boolean
}

export const addInstructor = createAsyncThunk<AddInstructorResponseType, AddInstructorRequestType>(
  `${sliceName}/instructor`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}instructor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        },
        body: JSON.stringify(args)
      })
        .then((res) => res.json())
        .then((res) => {
          return res
        })

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  }
)

export const configSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setAPISetting: (state, action: { payload: ApiSettingsType }) => {
      state.setting = action.payload
    },
    setViewStudent: (state, action: { payload: ViewStudentType }) => {
      state.view_student = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConfig.pending, (state) => {
      state.fetching = true
    })

    builder.addCase(fetchConfig.fulfilled, (state, action) => {
      state.fetching = false
      state.init = true

      const configPayload = action.payload.config

      state.logged_in = configPayload.logged_in
      state.rate_video = configPayload.rate_video
      state.is_admin = configPayload.is_admin
      state.is_instructor = configPayload.is_instructor
      state.rank = configPayload.rank
      state.instructor_id = configPayload.instructor_id
      state.access_level = configPayload.access_level
      state.ratingResponse = action.payload.ratingResponse
      state.override_builder_image = action.payload.config.override_builder_image
      state.override_help_image = action.payload.config.override_help_image
      state.override_live_image = action.payload.config.override_live_image
      state.override_upload_image = action.payload.config.override_upload_image
      state.override_video_image = action.payload.config.override_video_image
      state.need_select_instructor = action.payload.config.need_select_instructor
      state.instructors = action.payload.config.instructors
      state.instructor_approved = action.payload.config.instructor_approved
    })

    builder.addCase(addInstructor.fulfilled, (state, action) => {
      state.instructor_id = action.meta.arg.instructor_id
    })
  }
})

export const { setAPISetting, setViewStudent } = configSlice.actions
